import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';

import { SEO_DEFAULTS, SEO_TITLE_SUFFIX } from '../constants/seo.constants';
import { absoluteUrl, truncate } from '../utils/seo.utils';

export type SeoOgType = 'website' | 'product' | 'article';
export type SeoRobots = 'index,follow' | 'noindex,nofollow' | 'noindex,follow';
export type SeoTwitterCard = 'summary' | 'summary_large_image';

export interface SeoInput {
  title?: string;
  /** Si `true`, NO se appendea " — Perrón Store" al title. */
  rawTitle?: boolean;
  description?: string;
  image?: string;
  type?: SeoOgType;
  canonical?: string;
  robots?: SeoRobots;
  twitterCard?: SeoTwitterCard;
  locale?: string;
  jsonLd?: object | object[] | null;
}

const JSONLD_NODE_ID = 'perron-page-jsonld';
const CANONICAL_LINK_ID = 'perron-canonical';

@Injectable({ providedIn: 'root' })
export class SeoService {
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly document = inject(DOCUMENT);

  apply(input: SeoInput): void {
    const title = this.resolveTitle(input);
    const description = truncate(
      input.description?.trim() || SEO_DEFAULTS.defaultDescription,
      300,
    );
    const image = absoluteUrl(input.image || SEO_DEFAULTS.defaultImage);
    const type = input.type ?? 'website';
    const canonical = input.canonical ? absoluteUrl(input.canonical) : null;
    const robots = input.robots ?? 'index,follow';
    const twitterCard = input.twitterCard ?? SEO_DEFAULTS.twitterCard;
    const locale = input.locale ?? SEO_DEFAULTS.locale;

    this.title.setTitle(title);
    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({ name: 'robots', content: robots });

    this.meta.updateTag({ property: 'og:site_name', content: SEO_DEFAULTS.siteName });
    this.meta.updateTag({ property: 'og:type', content: type });
    this.meta.updateTag({ property: 'og:title', content: title });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:image', content: image });
    this.meta.updateTag({ property: 'og:locale', content: locale });
    if (canonical) {
      this.meta.updateTag({ property: 'og:url', content: canonical });
    } else {
      this.meta.removeTag('property="og:url"');
    }

    this.meta.updateTag({ name: 'twitter:card', content: twitterCard });
    this.meta.updateTag({ name: 'twitter:title', content: title });
    this.meta.updateTag({ name: 'twitter:description', content: description });
    this.meta.updateTag({ name: 'twitter:image', content: image });

    this.setCanonical(canonical);
    this.setJsonLd(input.jsonLd ?? null);
  }

  /**
   * Limpia tags volátiles antes de aplicar SEO de una nueva ruta. Sólo borra
   * lo que cambia entre páginas; deja intactos los defaults globales del
   * `index.html` (Organization JSON-LD, og:site_name, etc.) que se sobreescriben
   * en `apply()`.
   */
  reset(): void {
    this.removeJsonLd();
  }

  private resolveTitle(input: SeoInput): string {
    if (!input.title) return SEO_DEFAULTS.defaultTitle;
    if (input.rawTitle) return input.title;
    if (input.title.includes(SEO_DEFAULTS.siteName)) return input.title;
    return `${input.title}${SEO_TITLE_SUFFIX}`;
  }

  private setCanonical(href: string | null): void {
    const head = this.document.head;
    let link = this.document.getElementById(CANONICAL_LINK_ID) as HTMLLinkElement | null;
    if (!href) {
      if (link) link.remove();
      return;
    }
    if (!link) {
      link = this.document.createElement('link');
      link.id = CANONICAL_LINK_ID;
      link.rel = 'canonical';
      head.appendChild(link);
    }
    link.href = href;
  }

  private setJsonLd(payload: object | object[] | null): void {
    if (!payload) {
      this.removeJsonLd();
      return;
    }
    const json = Array.isArray(payload)
      ? { '@context': 'https://schema.org', '@graph': payload }
      : payload;
    let node = this.document.getElementById(JSONLD_NODE_ID) as HTMLScriptElement | null;
    if (!node) {
      node = this.document.createElement('script');
      node.id = JSONLD_NODE_ID;
      node.type = 'application/ld+json';
      this.document.head.appendChild(node);
    }
    node.textContent = JSON.stringify(json);
  }

  private removeJsonLd(): void {
    const node = this.document.getElementById(JSONLD_NODE_ID);
    if (node) node.remove();
  }
}
